import React, { useState } from "react";
import { Box, Typography, Button, TextField, Select, MenuItem, FormControl, Paper, Divider, DialogTitle, DialogContent, DialogActions, IconButton, Alert, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import {
  ChatBubbleOutline,
  CheckCircleOutline,
  Print,
  Refresh,
  BarChart,
  AttachFile,
  Close,
  Delete,
  Create,
} from "@mui/icons-material";
import { samplePMCals } from "./pmCalData";

import ResponsiveDialog from "../../Component/ResponsiveDialog";
// ─── Calibration types ────────────────────────────────────────────────────────
const CAL_TYPES = [
  { value: "", label: "Select Type" },
  { value: "internal", label: "Internal" },
  { value: "external", label: "External Vendor" },
];

// ─── Component ────────────────────────────────────────────────────────────────
const PMCalFeedback = () => {
  // Filter only in-progress PM-Cals for feedback
  const availablePMCals = samplePMCals.filter((p) => p.status === "in-progress");

  const [selectedId, setSelectedId] = useState("");
  const [calDate, setCalDate] = useState(new Date().toISOString().split("T")[0]);
  const [calType, setCalType] = useState("");
  const [calibratedBy] = useState("Admin User");
  const [notes, setNotes] = useState("");
  const [measurements, setMeasurements] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [submitted, setSubmitted] = useState(false);

  // Measurement dialog state
  const [measDialogOpen, setMeasDialogOpen] = useState(false);
  const [measParameter, setMeasParameter] = useState("");
  const [measUnit, setMeasUnit] = useState("");
  const [measExpected, setMeasExpected] = useState("");
  const [measActual, setMeasActual] = useState("");
  const [measTolerance, setMeasTolerance] = useState("");
  const [measNotes, setMeasNotes] = useState("");
  const [measError, setMeasError] = useState("");

  // Certificate dialog state
  const [certDialogOpen, setCertDialogOpen] = useState(false);
  const [certNumber, setCertNumber] = useState("");
  const [certVendor, setCertVendor] = useState("");
  const [certExpiry, setCertExpiry] = useState("");
  const [certFileName, setCertFileName] = useState("");
  const [certError, setCertError] = useState("");

  // Electronic signature dialog state
  const [sigDialogOpen, setSigDialogOpen] = useState(false);
  const [sigUsername, setSigUsername] = useState("");
  const [sigPassword, setSigPassword] = useState("");
  const [sigError, setSigError] = useState("");

  const selectedPMCal = samplePMCals.find((p) => p.id === selectedId);

  // ── Measurement handlers ──
  const openMeasDialog = () => {
    if (!selectedId) return;
    setMeasParameter("");
    setMeasUnit("");
    setMeasExpected("");
    setMeasActual("");
    setMeasTolerance("");
    setMeasNotes("");
    setMeasError("");
    setMeasDialogOpen(true);
  };

  const addMeasurement = () => {
    if (!measParameter || !measExpected || !measActual) {
      setMeasError("Please fill in all required fields");
      return;
    }
    const expected = parseFloat(measExpected);
    const actual = parseFloat(measActual);
    const tolerance = parseFloat(measTolerance) || 0;
    const diff = Math.abs(expected - actual);
    const result = diff <= tolerance ? "Pass" : "Fail";

    setMeasurements((prev) => [
      ...prev,
      {
        id: Date.now().toString(),
        parameter: measParameter,
        unit: measUnit,
        expected,
        actual,
        tolerance,
        result,
        notes: measNotes,
      },
    ]);
    setMeasDialogOpen(false);
  };

  const removeMeasurement = (id) => {
    setMeasurements((prev) => prev.filter((m) => m.id !== id));
  };

  // ── Certificate handlers ──
  const openCertDialog = () => {
    if (!selectedId) return;
    setCertNumber("");
    setCertVendor("");
    setCertExpiry("");
    setCertFileName("");
    setCertError("");
    setCertDialogOpen(true);
  };

  const attachCertificate = () => {
    if (!certFileName) {
      setCertError("Please enter a certificate file name");
      return;
    }
    setCertificates((prev) => [
      ...prev,
      {
        id: Date.now().toString(),
        filename: certFileName,
        certificateNumber: certNumber,
        vendor: certVendor,
        expiry: certExpiry,
        uploadedAt: new Date().toISOString(),
      },
    ]);
    setCertDialogOpen(false);
  };

  const removeCertificate = (id) => {
    setCertificates((prev) => prev.filter((c) => c.id !== id));
  };

  // ── Submit / Signature handlers ──
  const handleSubmitClick = () => {
    if (!selectedId || !calDate || !calType) return;
    if (measurements.length === 0) {
      alert("Please add at least one measurement");
      return;
    }
    setSigUsername("");
    setSigPassword("");
    setSigError("");
    setSigDialogOpen(true);
  };

  const confirmSignature = () => {
    if (!sigUsername || !sigPassword) {
      setSigError("Please fill in all signature fields");
      return;
    }
    setSubmitted(true);
    setSigDialogOpen(false);
  };

  // ── Print calibration label ──
  const handlePrintLabel = () => {
    if (!selectedPMCal) return;
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
      <head>
        <title>Calibration Label - ${selectedPMCal.assetName}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 0; padding: 20px; font-size: 12px; }
          .label { width: 4in; height: 2in; border: 2px solid #000; padding: 10px; box-sizing: border-box; }
          .header { text-align: center; font-weight: bold; margin-bottom: 10px; font-size: 16px; }
          .field { margin: 5px 0; }
        </style>
      </head>
      <body>
        <div class="label">
          <div class="header">CALIBRATED</div>
          <div class="field"><strong>Asset:</strong> ${selectedPMCal.assetName}</div>
          <div class="field"><strong>Cal Date:</strong> ${calDate}</div>
          <div class="field"><strong>Next Due:</strong> ${selectedPMCal.dueDate}</div>
          <div class="field"><strong>By:</strong> ${calibratedBy}</div>
          <div class="field"><strong>PM-Cal:</strong> ${selectedPMCal.id}</div>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // ── Reset ──
  const handleReset = () => {
    setSelectedId("");
    setCalDate(new Date().toISOString().split("T")[0]);
    setCalType("");
    setNotes("");
    setMeasurements([]);
    setCertificates([]);
    setSubmitted(false);
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f0f2f5" }}>
      {/* ── Page Header ── */}
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #e0e4ea", px: 3.5, py: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              bgcolor: "#e8f0fe",
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ChatBubbleOutline sx={{ color: "#2362B4", fontSize: 20 }} />
          </Box>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: "#2362B4" }}>
            PM-Cal Feedback
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 13, color: "#6b7280", mt: 0.3, ml: "46px" }}>
          Enter calibration feedback and measurement results
        </Typography>
      </Box>

      <Box sx={{ p: 2.5 }}>
        {/* ── Top Row: Select + Details ── */}
        <Box sx={{ display: "flex", gap: 2.5, mb: 2.5 }}>
          {/* Select PM-Cal */}
          <Paper
            elevation={0}
            sx={{ flex: 1, border: "1px solid #e0e4ea", borderRadius: 2, p: 2.5 }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#1a2333", mb: 2 }}>
              Select PM-Cal for Feedback
            </Typography>

            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#2362B4", mb: 0.8 }}>
              PM-Cal Record <span style={{ color: "#C62828" }}>*</span>
            </Typography>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <Select
                value={selectedId}
                onChange={(e) => {
                  setSelectedId(e.target.value);
                  setSubmitted(false);
                  setMeasurements([]);
                  setCertificates([]);
                }}
                displayEmpty
                sx={{ fontSize: 14, borderRadius: 1.5 }}
              >
                <MenuItem value="">Select PM-Cal</MenuItem>
                {availablePMCals.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.id} - {p.assetName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedPMCal && (
              <Box sx={{ mt: 1 }}>
                <Typography sx={{ fontSize: 13, mb: 0.5 }}>
                  <strong>Asset:</strong> {selectedPMCal.assetName}
                </Typography>
                <Typography sx={{ fontSize: 13, mb: 0.5 }}>
                  <strong>Tool:</strong> {selectedPMCal.toolName || "N/A"}
                </Typography>
                <Typography sx={{ fontSize: 13, mb: 0.5 }}>
                  <strong>Frequency:</strong> {selectedPMCal.frequency || "N/A"}
                </Typography>
                <Typography sx={{ fontSize: 13, mb: 0.5 }}>
                  <strong>Planned Date:</strong> {selectedPMCal.startDate}
                </Typography>
                <Typography sx={{ fontSize: 13 }}>
                  <strong>Due Date:</strong> {selectedPMCal.dueDate}
                </Typography>
              </Box>
            )}
          </Paper>

          {/* Calibration Details */}
          <Paper
            elevation={0}
            sx={{ flex: 1, border: "1px solid #e0e4ea", borderRadius: 2, p: 2.5 }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#1a2333", mb: 2 }}>
              Calibration Details
            </Typography>

            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#2362B4", mb: 0.8 }}>
                  Calibration Date <span style={{ color: "#C62828" }}>*</span>
                </Typography>
                <TextField
                  type="date"
                  size="small"
                  fullWidth
                  value={calDate}
                  onChange={(e) => setCalDate(e.target.value)}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#2362B4", mb: 0.8 }}>
                  Calibration Type <span style={{ color: "#C62828" }}>*</span>
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={calType}
                    onChange={(e) => setCalType(e.target.value)}
                    displayEmpty
                    sx={{ borderRadius: 1.5 }}
                  >
                    {CAL_TYPES.map((t) => (
                      <MenuItem key={t.value} value={t.value}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#2362B4", mb: 0.8 }}>
                Calibrated By <span style={{ color: "#C62828" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={calibratedBy}
                InputProps={{ readOnly: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#2362B4", mb: 0.8 }}>
                Calibration Notes
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Enter any notes or observations"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
              />
            </Box>

            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Button
                variant="contained"
                startIcon={<BarChart />}
                onClick={openMeasDialog}
                disabled={!selectedId}
                sx={{
                  bgcolor: "#2362B4",
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 1.5,
                  "&:hover": { bgcolor: "#1B4F96" },
                }}
              >
                Add Measurements
              </Button>
              <Button
                variant="contained"
                startIcon={<AttachFile />}
                onClick={openCertDialog}
                disabled={!selectedId}
                sx={{
                  bgcolor: "#00ACC1",
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 1.5,
                  "&:hover": { bgcolor: "#00838f" },
                }}
              >
                Attach Certificate
              </Button>
            </Box>
          </Paper>
        </Box>

        {/* ── Measurements & Certificates ── */}
        {selectedId && (
          <Paper
            elevation={0}
            sx={{ border: "1px solid #e0e4ea", borderRadius: 2, p: 2.5, mb: 2.5 }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#1a2333", mb: 2 }}>
              Measurements &amp; Certificates
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: "flex", gap: 4 }}>
              {/* Measurement Data */}
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1a2333", mb: 1 }}>
                  Measurement Data
                </Typography>
                {measurements.length === 0 ? (
                  <Typography sx={{ fontSize: 13, color: "#9ca3af" }}>
                    No measurements added
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: "#f8f9fb" }}>
                          <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Parameter</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Expected</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Actual</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Tolerance</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Result</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {measurements.map((m) => (
                          <TableRow key={m.id}>
                            <TableCell sx={{ fontSize: 12 }}>{m.parameter}</TableCell>
                            <TableCell sx={{ fontSize: 12 }}>
                              {m.expected} {m.unit}
                            </TableCell>
                            <TableCell sx={{ fontSize: 12 }}>
                              {m.actual} {m.unit}
                            </TableCell>
                            <TableCell sx={{ fontSize: 12 }}>
                              ±{m.tolerance} {m.unit}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={m.result}
                                size="small"
                                sx={{
                                  bgcolor: m.result === "Pass" ? "#dcfce7" : "#fee2e2",
                                  color: m.result === "Pass" ? "#15803d" : "#b91c1c",
                                  fontWeight: 600,
                                  fontSize: 11,
                                  height: 22,
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => removeMeasurement(m.id)}
                                sx={{ color: "#C62828" }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>

              <Divider orientation="vertical" flexItem />

              {/* Certificates */}
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1a2333", mb: 1 }}>
                  Attached Certificates
                </Typography>
                {certificates.length === 0 ? (
                  <Typography sx={{ fontSize: 13, color: "#9ca3af" }}>
                    No certificates attached
                  </Typography>
                ) : (
                  certificates.map((c) => (
                    <Box
                      key={c.id}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1.5,
                        p: 1.5,
                        border: "1px solid #e0e4ea",
                        borderRadius: 1,
                      }}
                    >
                      <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
                          {c.filename}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: "#9ca3af" }}>
                          {c.certificateNumber
                            ? `Cert #: ${c.certificateNumber}`
                            : "No certificate number"}
                          {c.vendor ? ` | Vendor: ${c.vendor}` : ""}
                          {c.expiry ? ` | Expires: ${c.expiry}` : ""}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => removeCertificate(c.id)}
                        sx={{ color: "#C62828" }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  ))
                )}
              </Box>
            </Box>
          </Paper>
        )}

        {/* ── Action Buttons ── */}
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button
            variant="contained"
            startIcon={<CheckCircleOutline />}
            onClick={submitted ? undefined : handleSubmitClick}
            disabled={!selectedId}
            sx={{
              bgcolor: "#2e7d32",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 1.5,
              "&:hover": { bgcolor: "#1b5e20" },
            }}
          >
            {submitted ? "Feedback Submitted ✓" : "Submit Feedback"}
          </Button>
          <Button
            variant="contained"
            startIcon={<Print />}
            onClick={handlePrintLabel}
            disabled={!selectedId}
            sx={{
              bgcolor: "#E65100",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 1.5,
              "&:hover": { bgcolor: "#bf360c" },
            }}
          >
            Print Calibration Label
          </Button>
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={handleReset}
            sx={{
              bgcolor: "#616161",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 1.5,
              "&:hover": { bgcolor: "#424242" },
            }}
          >
            Reset
          </Button>
        </Box>
      </Box>

      {/* ── Measurement Dialog ── */}
      <ResponsiveDialog open={measDialogOpen} onClose={() => setMeasDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            bgcolor: "#2362B4",
            color: "#fff",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1.5,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 16 }}>Add Measurement Data</Typography>
          <IconButton onClick={() => setMeasDialogOpen(false)} sx={{ color: "#fff" }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {measError && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
              {measError}
            </Alert>
          )}
          <Box sx={{ display: "flex", gap: 2, mb: 2, mt: 1 }}>
            <TextField
              label="Parameter *"
              size="small"
              fullWidth
              value={measParameter}
              onChange={(e) => setMeasParameter(e.target.value)}
              placeholder="e.g., Pressure, Temperature"
            />
            <TextField
              label="Unit *"
              size="small"
              fullWidth
              value={measUnit}
              onChange={(e) => setMeasUnit(e.target.value)}
              placeholder="e.g., PSI, °C"
            />
          </Box>
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <TextField
              label="Expected Value *"
              type="number"
              size="small"
              fullWidth
              value={measExpected}
              onChange={(e) => setMeasExpected(e.target.value)}
            />
            <TextField
              label="Actual Value *"
              type="number"
              size="small"
              fullWidth
              value={measActual}
              onChange={(e) => setMeasActual(e.target.value)}
            />
            <TextField
              label="Tolerance (±)"
              type="number"
              size="small"
              fullWidth
              value={measTolerance}
              onChange={(e) => setMeasTolerance(e.target.value)}
            />
          </Box>
          <TextField
            label="Notes"
            multiline
            rows={2}
            fullWidth
            size="small"
            value={measNotes}
            onChange={(e) => setMeasNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            onClick={addMeasurement}
            sx={{
              bgcolor: "#2362B4",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { bgcolor: "#1B4F96" },
            }}
          >
            Add Measurement
          </Button>
          <Button onClick={() => setMeasDialogOpen(false)} sx={{ textTransform: "none" }}>
            Close
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      {/* ── Certificate Dialog ── */}
      <ResponsiveDialog open={certDialogOpen} onClose={() => setCertDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            bgcolor: "#2362B4",
            color: "#fff",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1.5,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
            Attach Calibration Certificate
          </Typography>
          <IconButton onClick={() => setCertDialogOpen(false)} sx={{ color: "#fff" }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {certError && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
              {certError}
            </Alert>
          )}
          <Box sx={{ mb: 2, mt: 1 }}>
            <TextField
              label="Certificate File Name *"
              size="small"
              fullWidth
              value={certFileName}
              onChange={(e) => setCertFileName(e.target.value)}
              placeholder="e.g., calibration-cert.pdf"
              helperText="Supported formats: PDF, JPG, PNG, DOC, DOCX"
            />
          </Box>
          <TextField
            label="Certificate Number"
            size="small"
            fullWidth
            value={certNumber}
            onChange={(e) => setCertNumber(e.target.value)}
            placeholder="Enter certificate number"
            sx={{ mb: 2 }}
          />
          <TextField
            label="Vendor/Lab"
            size="small"
            fullWidth
            value={certVendor}
            onChange={(e) => setCertVendor(e.target.value)}
            placeholder="Enter vendor or lab name"
            sx={{ mb: 2 }}
          />
          <TextField
            label="Certificate Expiry"
            type="date"
            size="small"
            fullWidth
            value={certExpiry}
            onChange={(e) => setCertExpiry(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AttachFile />}
            onClick={attachCertificate}
            sx={{
              bgcolor: "#2362B4",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { bgcolor: "#1B4F96" },
            }}
          >
            Attach Certificate
          </Button>
          <Button onClick={() => setCertDialogOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      {/* ── Electronic Signature Dialog ── */}
      <ResponsiveDialog open={sigDialogOpen} onClose={() => setSigDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            bgcolor: "#2362B4",
            color: "#fff",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1.5,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 16 }}>Electronic Signature</Typography>
          <IconButton onClick={() => setSigDialogOpen(false)} sx={{ color: "#fff" }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
            This action requires your electronic signature per FDA 21 CFR Part 11 requirements.
          </Alert>

          {sigError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {sigError}
            </Alert>
          )}

          <TextField
            label="Username *"
            size="small"
            fullWidth
            value={sigUsername}
            onChange={(e) => setSigUsername(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Password *"
            type="password"
            size="small"
            fullWidth
            value={sigPassword}
            onChange={(e) => setSigPassword(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Meaning of Signature"
            size="small"
            fullWidth
            value="I approve this calibration feedback"
            InputProps={{ readOnly: true }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            startIcon={<Create />}
            onClick={confirmSignature}
            sx={{
              bgcolor: "#2e7d32",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { bgcolor: "#1b5e20" },
            }}
          >
            Sign and Submit
          </Button>
          <Button onClick={() => setSigDialogOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
        </DialogActions>
      </ResponsiveDialog>
    </Box>
  );
};

export default PMCalFeedback;
